/**
 * ENG-9585: host-side skill body cache, and the resolvers that turn
 * `/host/refresh`'s `global_skills_manifest`, `shared_skills_manifest` and
 * `integration_skills_manifest` back into the entries the manager installs.
 *
 * WHAT THIS BUYS. The poll used to carry every skill body to every agent every
 * ~10s so the manager could render each `SKILL.md` and then discover, by hash,
 * that the file on disk was already identical. Measured on prod: `shared_skills`
 * 28.9%, `integration_skills` 18.6% and `global_skills` 3.5% of the average
 * `/host/refresh` body — 51.0% between them. Now the poll carries metadata plus
 * a hash and bodies are fetched once and kept.
 *
 * CONTENT-ADDRESSED, AND HOST-WIDE. The store is keyed on the content hash
 * alone — not on agent, code_name, scope or lane — so it dedupes four ways at
 * once: across every agent on the host (a global skill, and an integration's
 * catalog-seeded scope body, are byte-identical for all of them, so the first
 * agent's fetch serves the rest), across scopes (the same body published at
 * team and org level is stored once), across LANES (a body reachable both as a
 * root skill and as an integration scope is one file), and across time (a skill
 * renamed, or unpublished and restored, is never refetched). It lives at
 * `~/.augmented/_skills/`; the `_` prefix keeps `getRegisteredAgents` from
 * mistaking it for an agent directory.
 *
 * The integration lane is where the cross-agent dedupe pays most. Integration
 * child skills come from the globally-seeded catalog, so a host running six
 * agents that all have the same integration installed previously received six
 * identical copies of every scope body on every poll.
 *
 * A SEPARATE DIRECTORY FROM `_knowledge`, deliberately. Sharing one store would
 * be safe — both are sha256-addressed, and a collision between a knowledge body
 * and a skill body is the same event as any other sha256 collision — but it
 * would buy nothing (the two corpora do not overlap) and would have meant
 * renaming the live `_knowledge` directory, orphaning every body the fleet has
 * already cached.
 *
 * METADATA IS NEVER CACHED. Only bodies are. `code_name` decides the folder the
 * skill is written to, so a renamed skill must move even though its body — and
 * therefore its hash — is unchanged. Taking metadata fresh from the manifest
 * every poll makes that correct by construction.
 *
 * ALL-OR-NOTHING, AND THIS IS WHY THE RESOLVERS RETURN `null`. The knowledge
 * resolver fails soft per entry: an unresolvable body yields `content: null`
 * and the rendered skill is briefly missing a section. Skills cannot do that,
 * because the manager PRUNES — `planGlobalSkillSync` removes the on-disk folder
 * of any previously-installed root skill absent from the desired set, and the
 * integration sweep removes any `integration-<slug>/` folder this poll did not
 * name. A skill whose body failed to resolve is indistinguishable from one that
 * was unassigned. A single failed fetch would therefore DELETE real skill
 * folders and reinstall them on the next poll: churn, plus a window in which
 * the agent has no skills.
 *
 * TWO RESOLVERS, BECAUSE THERE ARE TWO PRUNE DOMAINS — and the split is the
 * design, not an accident of file layout. `resolveSkillsFromManifest` is
 * all-or-nothing across BOTH root lanes together, because `planGlobalSkillSync`
 * takes their union and reconciles it against ONE set of bare-named folders:
 * resolving globals while failing shared would reconcile half a desired set
 * against all of the folders and delete the half it could not see.
 * `resolveIntegrationSkillsFromManifest` is all-or-nothing over a disjoint set
 * of `integration-<slug>/` folders swept by different code, so it stands alone.
 * Merging the two would mean one unreachable integration body stalling
 * root-skill sync — the strict rule punishing a failure it has no stake in.
 *
 * On failure the caller leaves that domain's fields absent. For the root lanes
 * that is ENG-7492's existing "the desired set is unknown, do not prune"
 * signal, reached by exactly the mechanism it was built for; for the
 * integration lane it means skipping the install loop AND the orphan sweep,
 * which must move together — skipping only the loop leaves an empty
 * `currentIntegrationSkillIds` and the sweep then deletes every integration
 * folder on the box.
 *
 * Cost of the strict rule: one unresolvable body delays that lane's sync by one
 * poll. Cost of the lenient one: deleting a skill the agent still has.
 */

import { createHash } from 'node:crypto';
import {
  existsSync,
  mkdirSync,
  readFileSync,
  readdirSync,
  renameSync,
  rmSync,
  statSync,
  utimesSync,
  writeFileSync,
} from 'node:fs';
import { join } from 'node:path';

/** One root skill as `/host/refresh` lists it in manifest mode. */
export interface SkillManifestEntry {
  code_name: string;
  display_name?: string;
  /** Present on shared skills only (agent | team | organization). */
  scope?: string;
  hash?: string;
}

/** A fully-resolved entry, the shape `planGlobalSkillSync` has always consumed. */
export interface ResolvedSkillEntry {
  code_name: string;
  display_name?: string;
  scope?: string;
  content: string;
}

/**
 * Hashes per fetch request. Must not exceed the API's `MAX_SKILL_FETCH`
 * (`packages/api/src/routes/host-runtime.ts`), which rejects a larger request
 * with a 400.
 */
export const SKILL_FETCH_CHUNK = 100;

/** Untouched-for-this-long cached bodies are swept. A wrong sweep costs one refetch. */
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** At most one sweep per process per day — this runs inside a ~10s poll loop. */
const SWEEP_INTERVAL_MS = 24 * 60 * 60 * 1000;
let lastSweepAt = 0;

/**
 * sha256 of a skill body, matching what Postgres generates for the manifest
 * (`skill_definitions.body_hash`, migration 20260830000003, via
 * `public.knowledge_content_hash`).
 *
 * sha256 and not md5 because THIS CACHE IS SHARED ACROSS TENANTS — one store
 * serves every agent on the host, which is most of what makes it worth having.
 * A collision therefore serves one org's agent another org's skill body, and
 * md5 collisions are cheaply constructible by anyone who can author a skill.
 *
 * UTF-8 explicitly, not the platform default: Postgres digests
 * `convert_to(body,'UTF8')`, and skill bodies are markdown full of em dashes
 * and smart quotes that are one code unit but three bytes. A mismatched
 * encoding would verify clean on ASCII skills and reject exactly the
 * prose-heavy ones.
 */
export function skillHash(content: string): string {
  return createHash('sha256').update(content, 'utf8').digest('hex');
}

function cachePath(cacheDir: string, hash: string): string {
  return join(cacheDir, hash);
}

/**
 * Read a cached body, or null.
 *
 * Touches the file's mtime on a hit so the sweep below measures LAST USE rather
 * than last write — otherwise a body read on every poll but never rewritten
 * would age out and be refetched for no reason.
 */
export function readCachedSkill(cacheDir: string, hash: string): string | null {
  const path = cachePath(cacheDir, hash);
  try {
    const content = readFileSync(path, 'utf-8');
    try {
      const now = new Date();
      utimesSync(path, now, now);
    } catch { /* touch is an optimisation, never a failure */ }
    return content;
  } catch {
    return null;
  }
}

/**
 * Outcome of a cache write. THREE STATES, NOT A BOOLEAN (CodeRabbit, PR #5171).
 *
 * `mismatch` and `write-failed` are opposite facts about the body and must
 * never be conflated. A mismatch means the CONTENT is untrustworthy and has to
 * be discarded. A write failure — a full disk, a read-only mount, a permissions
 * change — means the content is fine and only the persistence of it failed.
 *
 * The first draft returned a single boolean, so the resolver discarded a
 * verified body whenever the disk was full. Under the all-or-nothing rule that
 * is not a slow cache, it is skill sync stopping entirely for as long as the
 * disk problem lasts — and the log line said "hash mismatch", pointing whoever
 * investigated at the server.
 */
export type SkillCacheWriteResult = 'written' | 'mismatch' | 'write-failed';

/**
 * Store a body under its hash, but only if it actually hashes to that.
 *
 * The verification is the point: it is what makes the cache safe to trust on
 * every subsequent poll without re-checking. A truncated or mis-mapped response
 * is rejected here — where it costs one refetch — instead of being written once
 * and served as authoritative content for as long as the file survives.
 */
export function writeCachedSkill(
  cacheDir: string,
  hash: string,
  content: string,
): SkillCacheWriteResult {
  if (skillHash(content) !== hash) return 'mismatch';
  try {
    mkdirSync(cacheDir, { recursive: true });
    // Write-then-rename: a manager killed mid-write must not leave a truncated
    // body at a name that claims to be a verified hash.
    const tmp = `${cachePath(cacheDir, hash)}.tmp-${process.pid}`;
    writeFileSync(tmp, content, 'utf-8');
    renameSync(tmp, cachePath(cacheDir, hash));
    return 'written';
  } catch {
    return 'write-failed';
  }
}

/** Drop bodies untouched for CACHE_TTL_MS. Best-effort; rate-limited per process. */
export function sweepSkillCache(cacheDir: string, now = Date.now()): void {
  if (now - lastSweepAt < SWEEP_INTERVAL_MS) return;
  lastSweepAt = now;
  try {
    if (!existsSync(cacheDir)) return;
    for (const name of readdirSync(cacheDir)) {
      const path = join(cacheDir, name);
      try {
        if (now - statSync(path).mtimeMs > CACHE_TTL_MS) rmSync(path, { force: true });
      } catch { /* a body we cannot stat or remove is not worth failing a poll over */ }
    }
  } catch { /* non-fatal */ }
}

/** Resets the sweep rate-limiter. Test seam only. */
export function __resetSweepClock(): void {
  lastSweepAt = 0;
}

/**
 * One integration child skill as `/host/refresh` lists it in manifest mode.
 *
 * `hash` and `content` are mutually exclusive and exactly one is always
 * present. A blank-bodied child — which this lane DELIVERS rather than drops,
 * unlike the root lanes — arrives as `content: ''` with no hash, because
 * hashing an empty string would cost a round trip to learn nothing.
 */
export interface IntegrationSkillManifestEntry {
  plugin_slug: string;
  integration_slug?: string;
  skill_id: string;
  skill_name: string;
  hash?: string;
  /** Present INSTEAD of `hash` when the source body is blank. */
  content?: string;
}

/** A fully-resolved integration child, the shape `groupSkillsByIntegration` takes. */
export interface ResolvedIntegrationSkillEntry {
  plugin_slug: string;
  integration_slug?: string;
  skill_id: string;
  skill_name: string;
  content: string;
}

export interface ResolveSkillsArgs {
  /** The global lane's manifest. */
  globals: SkillManifestEntry[];
  /** The shared lane's manifest. */
  shared: SkillManifestEntry[];
  cacheDir: string;
  /** Fetches bodies for the given hashes. Chunking is handled here. */
  fetchContents: (hashes: string[]) => Promise<Array<{ hash: string; content: string }>>;
  log?: (msg: string) => void;
}

export interface ResolvedSkills {
  globals: ResolvedSkillEntry[];
  shared: ResolvedSkillEntry[];
}

/**
 * Gather bodies for `hashes`: cache hits first, then one chunked fetch for the
 * rest. Shared by both resolvers so the cache-read, verify-on-write and
 * chunking rules cannot drift between the lanes that depend on them.
 *
 * Returns whatever it could resolve. Deciding whether a partial map is
 * acceptable belongs to the caller, because that answer is a property of the
 * prune domain, not of the fetch — see the module header.
 *
 * `lane` only labels log lines. It is worth the parameter: both lanes log
 * "hash mismatch for <hash>", and without it the one diagnostic that says
 * whether the server is mis-mapping root bodies or integration bodies is
 * indistinguishable between them.
 */
async function collectBodies(
  hashes: string[],
  cacheDir: string,
  fetchContents: (hashes: string[]) => Promise<Array<{ hash: string; content: string }>>,
  lane: string,
  log?: (msg: string) => void,
): Promise<Map<string, string>> {
  const bodies = new Map<string, string>();
  const missing: string[] = [];
  for (const hash of hashes) {
    if (bodies.has(hash) || missing.includes(hash)) continue;
    const cached = readCachedSkill(cacheDir, hash);
    if (cached !== null) bodies.set(hash, cached);
    else missing.push(hash);
  }

  for (let i = 0; i < missing.length; i += SKILL_FETCH_CHUNK) {
    const chunk = missing.slice(i, i + SKILL_FETCH_CHUNK);
    try {
      for (const { hash, content } of await fetchContents(chunk)) {
        const written = writeCachedSkill(cacheDir, hash, content);
        // An unverified body is DROPPED, not served. The two ways to get here
        // are transport corruption and a server-side hash->body mapping bug,
        // and the second hands back another skill's content — which would then
        // be written to this skill's folder and read by the agent as its
        // instructions.
        if (written === 'mismatch') {
          log?.(`[${lane}] hash mismatch for ${hash} — skipping skill sync this poll`);
          continue;
        }
        // A body that verified but could not be PERSISTED is still correct, so
        // it is served for this poll and simply refetched next time. Dropping
        // it here would let a full disk stop skill sync outright, which is the
        // all-or-nothing rule punishing a fault it was never meant to cover.
        if (written === 'write-failed') {
          log?.(`[${lane}] cache write failed for ${hash} — serving it uncached this poll`);
        }
        bodies.set(hash, content);
      }
    } catch (err) {
      log?.(`[${lane}] fetch failed for ${chunk.length} entries: ${(err as Error).message}`);
    }
  }

  sweepSkillCache(cacheDir);
  return bodies;
}

/**
 * Turn both skill manifests into the entries the manager installs, fetching
 * only the bodies this host does not already have.
 *
 * Returns `null` if ANY entry in EITHER lane could not be resolved — see the
 * module header. The caller must then leave both payload fields absent rather
 * than substituting an empty array, because an empty array means "nothing is
 * assigned" and prunes.
 */
export async function resolveSkillsFromManifest(
  args: ResolveSkillsArgs,
): Promise<ResolvedSkills | null> {
  const { globals, shared, cacheDir, fetchContents, log } = args;

  const hashes: string[] = [];
  for (const entry of [...globals, ...shared]) {
    // A manifest entry with no hash cannot name a body. The API only emits
    // entries whose `body_hash` is non-null (a blank body produces no usable
    // SKILL.md and is dropped server-side), so this is a malformed response
    // rather than a known-empty skill — and it is unresolvable either way.
    // Contrast the integration lane, where a blank body IS delivered and
    // arrives with inline `content` instead of a hash.
    if (!entry.hash) {
      log?.(`[skills] manifest entry '${entry.code_name}' has no hash — skipping skill sync`);
      return null;
    }
    hashes.push(entry.hash);
  }

  const bodies = await collectBodies(hashes, cacheDir, fetchContents, 'skills', log);

  const resolve = (entries: SkillManifestEntry[]): ResolvedSkillEntry[] | null => {
    const out: ResolvedSkillEntry[] = [];
    for (const e of entries) {
      const content = e.hash ? bodies.get(e.hash) : undefined;
      if (content === undefined) return null;
      out.push({
        code_name: e.code_name,
        ...(e.display_name !== undefined ? { display_name: e.display_name } : {}),
        ...(e.scope !== undefined ? { scope: e.scope } : {}),
        content,
      });
    }
    return out;
  };

  const resolvedGlobals = resolve(globals);
  const resolvedShared = resolve(shared);
  if (resolvedGlobals === null || resolvedShared === null) {
    log?.('[skills] one or more bodies unresolved — leaving on-disk skills untouched this poll');
    return null;
  }

  return { globals: resolvedGlobals, shared: resolvedShared };
}

export interface ResolveIntegrationSkillsArgs {
  manifest: IntegrationSkillManifestEntry[];
  cacheDir: string;
  /** Fetches bodies for the given hashes. Chunking is handled here. */
  fetchContents: (hashes: string[]) => Promise<Array<{ hash: string; content: string }>>;
  log?: (msg: string) => void;
}

/**
 * Turn `integration_skills_manifest` into the entries the bundle builder takes,
 * fetching only the bodies this host does not already have.
 *
 * Returns `null` if ANY entry could not be resolved. The caller must then skip
 * BOTH the integration install loop and its orphan sweep — see the module
 * header. Returning `[]` instead would be read as "every integration was
 * uninstalled" and delete every `integration-<slug>/` folder on the box.
 *
 * An entry carrying inline `content` (a blank body) resolves to that content
 * directly and never reaches the cache. An entry carrying NEITHER a hash nor
 * content is a malformed response, not an empty skill, and fails the lane —
 * the API emits exactly one of the two for every entry, so neither present
 * means the response did not come from the code this resolver is paired with.
 */
export async function resolveIntegrationSkillsFromManifest(
  args: ResolveIntegrationSkillsArgs,
): Promise<ResolvedIntegrationSkillEntry[] | null> {
  const { manifest, cacheDir, fetchContents, log } = args;

  const hashes: string[] = [];
  for (const entry of manifest) {
    if (entry.hash) {
      hashes.push(entry.hash);
      continue;
    }
    if (typeof entry.content !== 'string') {
      log?.(
        `[integration-skills] manifest entry '${entry.plugin_slug}/${entry.skill_id}' has neither hash nor content — skipping integration skill sync`,
      );
      return null;
    }
  }

  const bodies = await collectBodies(
    hashes,
    cacheDir,
    fetchContents,
    'integration-skills',
    log,
  );

  const out: ResolvedIntegrationSkillEntry[] = [];
  for (const e of manifest) {
    const content = e.hash ? bodies.get(e.hash) : e.content;
    if (content === undefined) {
      log?.(
        '[integration-skills] one or more bodies unresolved — leaving on-disk integration skills untouched this poll',
      );
      return null;
    }
    out.push({
      plugin_slug: e.plugin_slug,
      ...(e.integration_slug !== undefined ? { integration_slug: e.integration_slug } : {}),
      skill_id: e.skill_id,
      skill_name: e.skill_name,
      content,
    });
  }
  return out;
}
